class AudioManager {
  private static instance: AudioManager;
  private currentAudio: HTMLAudioElement | null = null;
  private currentMessageId: number | null = null;
  private listeners: Map<number, () => void> = new Map();
  // Cache generated audio URLs so repeated plays don't re-generate TTS
  private cache: Map<number, string> = new Map();

  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  async playAudio(messageId: number, audioUrl: string, onStateChange?: (isPlaying: boolean) => void): Promise<void> {
    // Stop current audio if playing
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }

    // Notify previous message that it stopped
    if (this.currentMessageId !== null && this.listeners.has(this.currentMessageId)) {
      this.listeners.get(this.currentMessageId)?.();
    }

    // Create new audio element
    this.currentAudio = new Audio(audioUrl);
    this.currentMessageId = messageId;

    // Set up event listeners
    this.currentAudio.onended = () => {
      // Keep messageId available for listeners during dispatch
      const endedMessageId = this.currentMessageId;
      this.currentAudio = null;
      onStateChange?.(false);
      // Broadcast update while currentMessageId still points to the ended message
      // so mini players can react (e.g., fade out UI)
      this.dispatchUpdate("ended");
      // Now clear the message id
      this.currentMessageId = null;
    };

    this.currentAudio.onerror = () => {
      // Keep messageId available for listeners during dispatch
      const erroredMessageId = this.currentMessageId;
      this.currentAudio = null;
      onStateChange?.(false);
      // Broadcast update while currentMessageId still points to the errored message
      this.dispatchUpdate("error");
      // Now clear the message id
      this.currentMessageId = null;
    };

    // Store the state change callback
    if (onStateChange) {
      this.listeners.set(messageId, () => onStateChange(false));
    }

    // Play the audio
    await this.currentAudio.play();
    onStateChange?.(true);
    // Broadcast update
    this.dispatchUpdate("play");
  }

  pauseAudio(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.dispatchUpdate("pause");
    }
  }

  resumeAudio(): void {
    if (this.currentAudio && this.currentAudio.paused) {
      this.currentAudio.play();
      this.dispatchUpdate("resume");
    }
  }

  isPaused(messageId: number): boolean {
    return this.currentMessageId === messageId && this.currentAudio !== null && this.currentAudio.paused;
  }

  stopAudio(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    if (this.currentMessageId !== null && this.listeners.has(this.currentMessageId)) {
      this.listeners.get(this.currentMessageId)?.();
    }
    this.currentMessageId = null;
    this.dispatchUpdate("stop");
  }

  isPlaying(messageId: number): boolean {
    return this.currentMessageId === messageId && this.currentAudio !== null && !this.currentAudio.paused;
  }

  getCurrentMessageId(): number | null {
    return this.currentMessageId;
  }

  // Expose current audio reference (read-only usage by UI)
  getCurrentAudio(): HTMLAudioElement | null {
    return this.currentAudio;
  }

  // Cache helpers
  getCachedUrl(messageId: number): string | undefined {
    return this.cache.get(messageId);
  }

  setCachedUrl(messageId: number, url: string): void {
    this.cache.set(messageId, url);
  }

  clearCachedUrl(messageId: number): void {
    const url = this.cache.get(messageId);
    if (url && url.startsWith("blob:")) {
      try { URL.revokeObjectURL(url); } catch {}
    }
    this.cache.delete(messageId);
  }

  // Dispatch a global event so listeners (mini players) can update UI
  private dispatchUpdate(action: "play" | "pause" | "resume" | "stop" | "ended" | "error") {
    try {
      const detail = {
        action,
        messageId: this.currentMessageId,
        isPlaying: !!(this.currentAudio && !this.currentAudio.paused),
        currentTime: this.currentAudio?.currentTime ?? 0,
        duration: this.currentAudio?.duration ?? 0,
      };
      window.dispatchEvent(new CustomEvent("audioManagerUpdate", { detail }));
    } catch {}
  }
}

export default AudioManager;

